import React, { useEffect, useState, Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../public/css/style.css';
import './i18n';

// Eager load critical pages
import { HomePage } from './pages/HomePage';
import { LoginPage } from './pages/LoginPage';
import { SignupPage } from './pages/SignupPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { RedirectAfterLogin } from './pages/RedirectAfterLogin';
import { PageLoader } from './components/PageLoader';

// Lazy load non-critical pages
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const UserDashboard = lazy(() => import('./pages/UserDashboard'));
const EventDetailPage = lazy(() => import('./pages/EventDetailPage'));
const EventRegistrationPage = lazy(() => import('./pages/EventRegistrationPage'));
const TicketScanner = lazy(() => import('./pages/TicketScanner'));
const SocialGallery = lazy(() => import('./pages/SocialGallery'));
const VIPUpgrade = lazy(() => import('./pages/VIPUpgrade'));
const Donate = lazy(() => import('./pages/Donate'));
const MySchedule = lazy(() => import('./pages/MySchedule').then(module => ({ default: module.MySchedule })));
const Leaderboard = lazy(() => import('./pages/Leaderboard').then(module => ({ default: module.Leaderboard })));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess').then(module => ({ default: module.PaymentSuccess })));

import { ProtectedRoute } from './components/ProtectedRoute';
import { useSessionManager } from './utils/useSessionManager';
import { validateInputAutocomplete, monitorDOMForMissingAutocomplete } from './utils/inputValidator';

function AppWithSessionManager() {
  useSessionManager(); // Initialize session management

  const [events, setEvents] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Fetch initial data
    Promise.all([
      fetch('/api/events').then(r => r.json()),
      fetch('/api/pages').then(r => r.json()),
    ])
      .then(([eventsData, pagesData]) => {
        setEvents(eventsData.events || []);
        setPages(pagesData.pages || []);
      })
      .catch(err => console.error('Error fetching data:', err))
      .finally(() => setLoading(false));

    // Validate input autocomplete attributes
    const timer = setTimeout(() => {
      console.info('🔍 [INPUT VALIDATION] Checking for inputs without autocomplete attributes...');
      validateInputAutocomplete();
    }, 1000);

    // Monitor DOM for dynamically added inputs
    const domMonitor = monitorDOMForMissingAutocomplete();

    return () => {
      clearTimeout(timer);
      if (domMonitor) domMonitor.stop();
    };
  }, []);

  if (loading) {
    return <div className="text-center p-5">Loading...</div>;
  }

  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>
        <Route path="/" element={<HomePage events={events} pages={pages} />} />
        <Route path="/about" element={<Suspense fallback={<PageLoader />}><AboutPage /></Suspense>} />
        <Route path="/gallery" element={<Suspense fallback={<PageLoader />}><SocialGallery /></Suspense>} />
        <Route path="/vip" element={<Suspense fallback={<PageLoader />}><VIPUpgrade /></Suspense>} />
        <Route path="/donate" element={<Suspense fallback={<PageLoader />}><Donate /></Suspense>} />
        <Route path="/contact" element={<Suspense fallback={<PageLoader />}><ContactPage /></Suspense>} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/redirect-after-login" element={<RedirectAfterLogin />} />
        <Route path="/events/:eventId/register" element={<Suspense fallback={<PageLoader />}><EventRegistrationPage /></Suspense>} />
        <Route path="/events/:eventId" element={<Suspense fallback={<PageLoader />}><EventDetailPage /></Suspense>} />
        <Route path="/admin" element={<Suspense fallback={<PageLoader />}><AdminPage /></Suspense>} />
        <Route
          path="/admin/scanner"
          element={
            <Suspense fallback={<PageLoader />}>
              <ProtectedRoute requiredPermission="ticket.scanner">
                <TicketScanner />
              </ProtectedRoute>
            </Suspense>
          }
        />
        <Route
          path="/scanner"
          element={
            <Suspense fallback={<PageLoader />}>
              <ProtectedRoute requiredPermission="ticket.scanner">
                <TicketScanner />
              </ProtectedRoute>
            </Suspense>
          }
        />
        <Route 
          path="/admin-dashboard" 
          element={
            <Suspense fallback={<PageLoader />}>
              <ProtectedRoute requiredRole="admin">
                <AdminDashboard />
              </ProtectedRoute>
            </Suspense>
          } 
        />
        <Route 
          path="/user-dashboard" 
          element={
            <Suspense fallback={<PageLoader />}>
              <ProtectedRoute requiredRole="user">
                <UserDashboard />
              </ProtectedRoute>
            </Suspense>
          } 
        />
        <Route 
          path="/my-schedule" 
          element={
            <ProtectedRoute>
              <Suspense fallback={<PageLoader />}>
                <MySchedule />
              </Suspense>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/leaderboard" 
          element={
            <Suspense fallback={<PageLoader />}>
              <Leaderboard />
            </Suspense>
          } 
        />
        <Route 
          path="/payment-success" 
          element={
            <Suspense fallback={<PageLoader />}>
              <PaymentSuccess />
            </Suspense>
          } 
        />
      </Routes>
    </Router>
  );
}

ReactDOM.createRoot(document.getElementById('root')).render(
  <React.StrictMode>
    <AppWithSessionManager />
  </React.StrictMode>
);
